/**
 * Observabilidade frontend: Sentry SDK para captura de erros do browser.
 *
 * Activado apenas em produção (precisa de VITE_SENTRY_DSN no build).
 * Em dev, é no-op silencioso — não polui o GlitchTip com erros locais.
 *
 * Stack: erros vão para https://errors.bitsapiens.io (GlitchTip, project ai-humanerp-web —
 * nome do projeto no GlitchTip por confirmar/renomear à parte, fora deste repo).
 */

import * as Sentry from "@sentry/react";

export function initObservability(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // Dev local sem DSN — silencioso.
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_ENVIRONMENT ?? "production",
    release: import.meta.env.VITE_APP_VERSION ?? "unknown",

    // v11: spans em streaming por defeito, formato que o GlitchTip não conhece. `static` mantém o modelo de transacções do v10.
    traceLifecycle: "static",

    integrations: [
      // v11 mudou o ciclo de sessão por defeito para `page`; `route` é o comportamento anterior (nova sessão por navegação).
      Sentry.browserSessionIntegration({ lifecycle: "route" }),
      // Captura navegação, clicks, requests automáticos.
      Sentry.browserTracingIntegration(),
      // Replay opcional — gravação do que o utilizador viu antes do erro.
      // Desactivado por defeito (pesado em bandwidth). Liga via env var.
      ...(import.meta.env.VITE_SENTRY_REPLAY === "true"
        ? [
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            }),
          ]
        : []),
    ],

    // 10% das transactions viram traces (cap para evitar overhead em prod).
    tracesSampleRate: Number(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0.1",
    ),
    // Não capturar request bodies (podem ter dados sensíveis). O v11 trocou `sendDefaultPii` por `dataCollection`, cujo
    // valor por defeito é permissivo; isto fixa o baseline restritivo do v10 (documentado no guia de migração do SDK).
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
        response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
      },
      httpBodies: [],
      urlQueryParams: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      graphQL: { document: false, variables: false },
    },

    // Filtros antes de enviar.
    beforeSend(event) {
      // Tira payloads de POST/PATCH (podem ter dados do colaborador).
      if (event.request?.data) {
        delete event.request.data;
      }
      // Tira cookies.
      if (event.request?.cookies) {
        delete event.request.cookies;
      }
      return event;
    },

    // Ignora erros conhecidos que são ruído.
    ignoreErrors: [
      // Extensões do browser
      "top.GLOBALS",
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Network blips
      "NetworkError when attempting to fetch resource",
      "Failed to fetch",
      // Auth — não é erro de aplicação, é fluxo normal
      "Unauthorized",
      "401",
    ],
  });
}
